import { useContext } from "react";
import { Control, Controller, UseFormRegister, useForm } from "react-hook-form";
import { HiLockClosed } from "react-icons/hi";

import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { Divider, Input } from "components/common/basic";
import {
  AddressSearch,
  BankAccountFormValues,
  BankAccountSection,
} from "components/common/composite";
import { FreelanceProfileFragment, IdentityFragment } from "graphql/fragments";
import {
  useCreateFreelanceProfileMutation,
  useUpdateFreelanceProfileMutation,
} from "graphql/mutations";
import { CurrencyEnum, FreelanceTypeEnum } from "graphql/types";
import { useErrorLogger } from "hooks";
import { AuthContext } from "providers/Authentication";
import { useTranslation } from "translations";
import {
  bankAccountNumberValidationSchema,
  getAlphanumericValueFromString,
} from "utils";
import { tw } from "utils/tw";

interface FormValues {
  id?: string;
  freelanceType: FreelanceTypeEnum;
  firstName: string;
  lastName: string;
  personalNumber: string;
  address: {
    line1: string;
    line2?: string;
    postalCode: string;
    city: string;
    county?: string;
    country: string;
  };
  defaultBankAccount?: BankAccountFormValues;
}

export interface Props {
  onSuccessfulEdit: (freelanceProfile: FreelanceProfileFragment) => void;
  formId: string;
  freelanceProfile?: FreelanceProfileFragment;
  identity?: IdentityFragment;
}

export default ({
  onSuccessfulEdit,
  formId,
  freelanceProfile,
  identity,
}: Props): React.JSX.Element => {
  const showBankAccount = !freelanceProfile;

  const { createFreelanceProfile } = useCreateFreelanceProfileMutation();
  const { updateFreelanceProfile } = useUpdateFreelanceProfileMutation();

  const { session } = useContext(AuthContext);
  const { t } = useTranslation("common");

  const validationSchema = Yup.object({
    freelanceType: Yup.string<FreelanceTypeEnum>().required(),
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    personalNumber: Yup.string()
      .required()
      .test(
        "isValidPersonalNumber",
        t(
          "freelanceProfileForm_individual.error.notValidPersonalNumber",
          "Please provide a valid personal number"
        ),
        (value: unknown) => {
          if (identity) return true;

          if (typeof value !== "string") return false;

          const validFormats = {
            NO: /^\d{11}$/,
            SE: /^(\d{6}|\d{8})[-+]\d{4}$/,
          };
          return Object.values(validFormats).some((regex) => regex.test(value));
        }
      ),
    address: Yup.object({
      line1: Yup.string().trim().required(),
      postalCode: Yup.string().trim().required(),
      city: Yup.string().trim().required(),
      country: Yup.string().trim().required(),
    }).required(),
    defaultBankAccount: Yup.object({
      accountNumber: bankAccountNumberValidationSchema(t)
        .required()
        .matches(
          /^\d+$/,
          t(
            "freelanceProfileForm_individual.accountNumber.error.invalid",
            "The bank account number should only contain numbers"
          )
        ),
      currency: Yup.string().required().oneOf(Object.values(CurrencyEnum)),
    })
      .default(undefined) // Makes children optional
      .optional(),
  });

  const defaultAddress =
    freelanceProfile?.address ?? session?.account.address ?? {};
  const defaultValues = {
    id: freelanceProfile?.id,
    freelanceType: FreelanceTypeEnum.Individual,
    firstName: identity?.firstName ?? "",
    lastName: identity?.lastName ?? "",
    personalNumber: identity ? "**********" : "",
    address: {
      line1: defaultAddress.line1 ?? "",
      postalCode: defaultAddress.postalCode ?? "",
      city: defaultAddress.city ?? "",
      country: defaultAddress.country ?? "",
    },
  };

  const {
    formState: { errors },
    handleSubmit,
    control,
    register,
    trigger,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });
  const { reportErrors } = useErrorLogger();
  reportErrors(errors);

  const onSubmit = ({
    firstName,
    lastName,
    personalNumber,
    address,
    defaultBankAccount,
  }: FormValues) => {
    const attributes = {
      ...(identity ? {} : { firstName, lastName, personalNumber }),
      address: {
        ...address,
        line1: address?.line1 ?? "",
        postalCode: address?.postalCode ?? "",
        city: address?.city ?? "",
        country: address?.country ?? "",
      },
    };

    if (freelanceProfile?.id)
      updateFreelanceProfile(
        { id: freelanceProfile.id, attributes },
        onSuccessfulEdit
      );
    else {
      if (!defaultBankAccount) return;
      const createAttributes = {
        ...attributes,
        bankAccount: {
          ...defaultBankAccount,
          accountNumber: getAlphanumericValueFromString(
            defaultBankAccount.accountNumber
          ),
          default: true,
        },
      };

      createFreelanceProfile(
        {
          attributes: {
            freelanceType: FreelanceTypeEnum.Individual,
            ...createAttributes,
          },
        },
        onSuccessfulEdit
      );
    }
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      className={tw("w-full", "flex", "flex-col", "space-y-4")}
    >
      <h2 className={tw("text-lg", "font-bold", "text-gray-900")}>
        {t(
          "freelanceProfileForm_individual.cardHeading",
          "Let's get your freelance profile up and running"
        )}
      </h2>

      <p className={tw("text-sm")}>
        {t(
          "freelanceProfileForm_individual.cardSubHeading",
          "Please provide the information below to secure that we are able to pay you"
        )}
      </p>

      <Input
        id="firstName"
        {...register("firstName")}
        label={t(
          "freelanceProfileForm_individual.firstName.label",
          "First Name"
        )}
        TrailingIcon={identity && HiLockClosed}
        disabled={Boolean(identity)}
        errorMessage={errors.firstName?.message}
      />

      <Input
        id="lastName"
        {...register("lastName")}
        label={t("freelanceProfileForm_individual.lastName.label", "Last Name")}
        TrailingIcon={identity && HiLockClosed}
        disabled={Boolean(identity)}
        errorMessage={errors.lastName?.message}
      />

      <Input
        id="personalNumber"
        {...register("personalNumber")}
        label={t(
          "freelanceProfileForm_individual.personalNumber.label",
          "Personal number"
        )}
        TrailingIcon={identity && HiLockClosed}
        disabled={Boolean(identity)}
        errorMessage={errors.personalNumber?.message}
      />

      <Controller
        control={control}
        name="address"
        render={({ field }) => (
          <AddressSearch
            label={t(
              "freelanceProfileForm_individual.address.label",
              "Your home address"
            )}
            address={field.value}
            setAddress={field.onChange}
            errorMessage={
              errors.address &&
              t(
                "freelanceProfileForm_individual.address.error.required",
                "Address is required"
              )
            }
            country={["no"]}
          />
        )}
      />

      {showBankAccount && (
        <>
          <Divider />

          <BankAccountSection
            // TODO: Find a way to prevent typecasting
            control={control as unknown as Control<FreelanceProfileFragment>}
            register={
              register as unknown as UseFormRegister<FreelanceProfileFragment>
            }
            bankAccountErrors={errors.defaultBankAccount}
            triggerBankAccountValidation={() => trigger("defaultBankAccount")}
          />
        </>
      )}
    </form>
  );
};
